// ─── Tier definitions ─────────────────────────────────────────────────────────
// Ranges are fixed in MVP. Using seconds (integers) to avoid float issues.
// Tier price formula uses the END of the range: price = hourlyRate × (endMin / 60)
// (confirmed by Figma — spec text says "mid" but Figma values match end-of-range)

export const ROLL_REVIEW_TIERS = [
  { key: 'tier_1', startSec: 0,   endSec: 150, label: '0 – 2.5 min' },
  { key: 'tier_2', startSec: 150, endSec: 300, label: '2.5 – 5 min' },
  { key: 'tier_3', startSec: 300, endSec: 450, label: '5 – 7.5 min' },
  { key: 'tier_4', startSec: 450, endSec: 600, label: '7.5 – 10 min' },
] as const;

export type TierKey = typeof ROLL_REVIEW_TIERS[number]['key'];
export type RollReviewCurrency = 'USD' | 'BRL' | 'EUR';

// ─── Stripe minimums (cents per currency) ─────────────────────────────────────

export const STRIPE_MINIMUMS: Record<RollReviewCurrency, number> = {
  USD: 50,
  BRL: 250,
  EUR: 50,
};

export const CURRENCY_SYMBOL: Record<RollReviewCurrency, string> = {
  USD: '$',
  BRL: 'R$',
  EUR: '€',
};

// ─── Calculation ──────────────────────────────────────────────────────────────

/**
 * Auto-calculate a tier price from the instructor's hourly rate.
 * Uses the END of the tier range, matching the Figma-specified values.
 * e.g. $100/h × (2.5 min / 60) = $4.17  →  417 cents
 */
export function calcAutoPrice(endSec: number, hourlyRateCents: number): number {
  const endMin = endSec / 60;
  return Math.round(hourlyRateCents * (endMin / 60));
}

/**
 * Build the full default tiers array from a given hourly rate.
 * All tiers start as auto-calculated (not manual).
 */
export function buildAutoTiers(hourlyRateCents: number): PricingTier[] {
  return ROLL_REVIEW_TIERS.map(t => ({
    key:           t.key,
    rangeStartSec: t.startSec,
    rangeEndSec:   t.endSec,
    label:         t.label,
    priceCents:    calcAutoPrice(t.endSec, hourlyRateCents),
    isManual:      false,
  }));
}

/**
 * Recalculate only the non-manual tiers when the hourly rate changes.
 */
export function recalcAutoTiers(
  tiers: PricingTier[],
  hourlyRateCents: number,
): PricingTier[] {
  return tiers.map(tier =>
    tier.isManual
      ? tier
      : { ...tier, priceCents: calcAutoPrice(tier.rangeEndSec, hourlyRateCents) },
  );
}

/**
 * Format a cents value as a display string for a given currency.
 * e.g. 417 cents in USD → "$ 4.17"
 */
export function formatPriceCents(cents: number, currency: RollReviewCurrency): string {
  const symbol = CURRENCY_SYMBOL[currency];
  const value  = (cents / 100).toFixed(2);
  return `${symbol} ${value}`;
}

/**
 * Format the Stripe minimum for an error message.
 */
export function formatStripeMinimum(currency: RollReviewCurrency): string {
  return formatPriceCents(STRIPE_MINIMUMS[currency], currency);
}

/**
 * Detect the pricing tier for a given video duration.
 * Iterates from highest to lowest tier; returns the first tier where startSec <= duration.
 * This ensures boundary values map to the higher tier (e.g., 150s → tier_2, not tier_1).
 */
export function detectTierFromDurationSeconds(durationSeconds: number): TierKey {
  for (let i = ROLL_REVIEW_TIERS.length - 1; i >= 0; i--) {
    const tier = ROLL_REVIEW_TIERS[i];
    if (tier.startSec <= durationSeconds) {
      return tier.key;
    }
  }
  return 'tier_1';
}

/**
 * Get the pricing tier and price for a given video duration.
 * Returns the matching tier from the instructor's pricing config and its price.
 */
export function getTierPrice(
  pricing: RollReviewPricing,
  durationSeconds: number,
): { tier: PricingTier; priceCents: number } {
  const tierKey = detectTierFromDurationSeconds(durationSeconds);
  const tier = pricing.tiers.find(t => t.key === tierKey);

  if (!tier) {
    throw new Error(`Tier ${tierKey} not found in pricing configuration`);
  }

  return {
    tier,
    priceCents: tier.priceCents,
  };
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PricingTier {
  key:           TierKey;
  rangeStartSec: number;
  rangeEndSec:   number;
  label:         string;
  priceCents:    number;
  isManual:      boolean;
}

export interface RollReviewPricing {
  instructorId:     string;
  currency:         RollReviewCurrency;
  useHourlyBase:    boolean;
  hourlyRateCents:  number | null;
  tiers:            PricingTier[];
  updatedAt:        string | null;
}

// ─── DB row shape (from Supabase) ─────────────────────────────────────────────

export interface RollReviewPricingRow {
  id:                string;
  instructor_id:     string;
  currency:          string;
  use_hourly_base:   boolean;
  hourly_rate_cents: number | null;
  tiers:             RawTier[];
  updated_at:        string;
  created_at:        string;
}

export interface RawTier {
  range_start_sec: number;
  range_end_sec:   number;
  price_cents:     number;
  is_manual:       boolean;
}

// ─── Mappers ──────────────────────────────────────────────────────────────────

export function rowToDomain(row: RollReviewPricingRow): RollReviewPricing {
  const tiers: PricingTier[] = ROLL_REVIEW_TIERS.map((def, i) => {
    const raw = row.tiers[i];
    return {
      key:           def.key,
      rangeStartSec: def.startSec,
      rangeEndSec:   def.endSec,
      label:         def.label,
      priceCents:    raw?.price_cents  ?? 0,
      isManual:      raw?.is_manual    ?? false,
    };
  });

  return {
    instructorId:    row.instructor_id,
    currency:        (row.currency as RollReviewCurrency) ?? 'USD',
    useHourlyBase:   row.use_hourly_base,
    hourlyRateCents: row.hourly_rate_cents,
    tiers,
    updatedAt:       row.updated_at,
  };
}

export function domainToRow(
  pricing: RollReviewPricing,
): Omit<RollReviewPricingRow, 'id' | 'created_at' | 'updated_at'> {
  return {
    instructor_id:     pricing.instructorId,
    currency:          pricing.currency,
    use_hourly_base:   pricing.useHourlyBase,
    hourly_rate_cents: pricing.hourlyRateCents,
    tiers:             pricing.tiers.map(t => ({
      range_start_sec: t.rangeStartSec,
      range_end_sec:   t.rangeEndSec,
      price_cents:     t.priceCents,
      is_manual:       t.isManual,
    })),
  };
}
